import { UiSchema } from '@rjsf/utils';
import { MODEL_TYPES, Secret } from 'api/model';
import { getSecretsOptions } from 'api/workflows-steps/formSchema';
import OllamaBaseUrlWidget from 'components/form/OllamaBaseUrlWidget';
import SecretWidget from 'components/form/SecretWidget';
import { JSONSchema7 } from 'json-schema';
import ModelCardSelector from 'views/prompts-models/ModelCardSelector';
import ModelProviderSelector from 'views/prompts-models/ModelProviderSelector';

function getSchema(secrets?: Secret[]): JSONSchema7 {
  const secretsOptions = getSecretsOptions(secrets, 'api_key');

  const secretIdProperty: JSONSchema7 =
    secretsOptions.length > 0
      ? {
          type: 'string',
          title: 'API Key',
        }
      : {
          type: 'string',
          title: 'API Key (no secrets available)',
          default: '',
        };

  return {
    type: 'object',
    properties: {
      name: { type: 'string' },
      description: { type: 'string' },
      type: {
        type: 'string',
        enum: MODEL_TYPES,
        default: 'openai',
        title: 'Provider',
      },
      is_local: {
        type: 'boolean',
        title: 'Local model',
        description: 'Whether this model is a local model that can be used in offline mode.',
        default: false,
      },
    },
    required: ['name', 'type'],
    definitions: {
      max_tokens: {
        type: ['number', 'null'],
        title: 'Max number of tokens to generate.',
        description: 'Defaults to the model maximum if not set.',
      },
      model_select: {
        type: 'object',
        title: 'Model',
        properties: {
          model: {
            type: 'string',
          },
        },
        required: ['model'],
      },
      safety_settings: {
        type: 'object',
        title: 'Safety Settings',
        // TODO: Add tooltips/labels to the range values instead of description
        description: '0 - Block none, 1 - Block few, 2 - Block some, 3 - Block most',
        properties: {
          harassment: {
            type: 'integer',
            minimum: 0,
            maximum: 3,
            title: 'Harassment',
            default: 1,
            enum: [0, 1, 2, 3],
          },
          hate_speech: {
            type: 'integer',
            minimum: 0,
            maximum: 3,
            title: 'Hate Speech',
            default: 1,
            enum: [0, 1, 2, 3],
          },
          sexually_explicit: {
            type: 'integer',
            minimum: 0,
            maximum: 3,
            title: 'Sexually Explicit',
            default: 1,
            enum: [0, 1, 2, 3],
          },
          dangerous_content: {
            type: 'integer',
            minimum: 0,
            maximum: 3,
            title: 'Dangerous Content',
            default: 1,
            enum: [0, 1, 2, 3],
          },
        },
      },
    },
    allOf: [
      {
        if: {
          properties: { type: { const: 'openai' } },
        },
        then: {
          properties: {
            secret_id: secretIdProperty,
            settings: {
              type: 'object',
              title: 'Settings',
              properties: {
                max_tokens: { $ref: '#/definitions/max_tokens' },
                openai_api_base: { type: ['string', 'null'] },
                openai_proxy: { type: ['string', 'null'] },
                model: { type: ['string'] },
              },
              required: ['model'],
            },
          },
          required: ['name', 'type', 'secret_id'],
        },
      },
      {
        if: {
          properties: { type: { const: 'anthropic' } },
        },
        then: {
          properties: {
            secret_id: secretIdProperty,
            settings: {
              type: 'object',
              title: 'Settings',
              properties: {
                max_tokens: { $ref: '#/definitions/max_tokens' },
                model: { type: ['string'] },
              },
              required: ['model'],
            },
          },
          required: ['name', 'type', 'secret_id'],
        },
      },
      {
        if: {
          properties: { type: { const: 'google' } },
        },
        then: {
          properties: {
            secret_id: secretIdProperty,
            settings: {
              type: 'object',
              title: 'Settings',
              properties: {
                max_tokens: { $ref: '#/definitions/max_tokens' },
                safety_settings: { $ref: '#/definitions/safety_settings' },
                model: { type: ['string'] },
              },
              required: ['safety_settings', 'model'],
            },
          },
          required: ['name', 'type', 'secret_id'],
        },
      },
      {
        if: {
          properties: { type: { const: 'vertex' } },
        },
        then: {
          properties: {
            settings: {
              type: 'object',
              title: 'Settings',
              properties: {
                max_tokens: { $ref: '#/definitions/max_tokens' },
                location: {
                  type: 'string',
                  enum: ['global', 'us-central1', 'us-west4', 'europe-west3', 'europe-central2'],
                },
                project_id: { type: 'string' },
                safety_settings: { $ref: '#/definitions/safety_settings' },
                model: { type: ['string'] },
              },
              required: ['location', 'project_id', 'safety_settings', 'model'],
            },
          },
          required: ['name', 'type'],
        },
      },
      {
        if: {
          properties: { type: { const: 'bedrock' } },
        },
        then: {
          properties: {
            settings: {
              type: 'object',
              title: 'Settings',
              properties: {
                max_tokens: { $ref: '#/definitions/max_tokens' },
                model: { type: ['string'] },
              },
              required: ['model'],
            },
          },
          required: ['name', 'type'],
        },
      },
      {
        if: {
          properties: { type: { const: 'perplexity' } },
        },
        then: {
          properties: {
            secret_id: secretIdProperty,
            settings: {
              type: 'object',
              title: 'Settings',
              properties: {
                max_tokens: { $ref: '#/definitions/max_tokens' },
                model: { type: ['string'] },
              },
              required: ['model'],
            },
          },
          required: ['name', 'type', 'secret_id'],
        },
      },
      {
        if: {
          properties: { type: { const: 'xai' } },
        },
        then: {
          properties: {
            secret_id: secretIdProperty,
            settings: {
              type: 'object',
              title: 'Settings',
              properties: {
                max_tokens: { $ref: '#/definitions/max_tokens' },
                model: { type: ['string'] },
              },
              required: ['model'],
            },
          },
          required: ['name', 'type', 'secret_id'],
        },
      },
      {
        if: {
          properties: { type: { const: 'ollama' } },
        },
        then: {
          properties: {
            settings: {
              type: 'object',
              title: 'Settings',
              properties: {
                base_url: {
                  type: 'string',
                  title: 'Base URL',
                  description: 'The base URL of your Ollama instance (e.g., http://localhost:11434).',
                },
                max_tokens: { $ref: '#/definitions/max_tokens' },
                model: { type: ['string'] },
              },
              required: ['base_url', 'model'],
            },
          },
          required: ['name', 'type'],
        },
      },
    ],
  };
}

const uiSchema: UiSchema = {
  type: {
    'ui:widget': ModelProviderSelector,
  },
  secret_id: {
    'ui:widget': SecretWidget,
    'ui:options': {
      types: ['api_key'],
    },
  },
  settings: {
    model: {
      'ui:widget': ModelCardSelector,
    },
    base_url: {
      'ui:widget': OllamaBaseUrlWidget,
    },
    safety_settings: {
      harassment: {
        'ui:widget': 'range',
      },
      hate_speech: {
        'ui:widget': 'range',
      },
      sexually_explicit: {
        'ui:widget': 'range',
      },
      dangerous_content: {
        'ui:widget': 'range',
      },
    },
  },
};

export { getSchema, uiSchema };
